/**
 * SidebarCustomizerPinGate — mały modal proszący o PIN przed otwarciem
 * SidebarCustomizerModal (edytora widoczności/kolejności modułów).
 * Bezstanowo, zapamiętuje odblokowanie w sessionStorage — 1x na sesję.
 */
import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import PortalOverlay from '../common/PortalOverlay';

const API = process.env.REACT_APP_BACKEND_URL;
const SESSION_KEY = 'sidebar_customizer_unlocked';

export const isCustomizerUnlocked = () => sessionStorage.getItem(SESSION_KEY) === '1';

const PinGate = ({ open, userId, onSuccess, onCancel }) => {
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setPin(''); setError('');
      setTimeout(() => inputRef.current?.focus(), 60);
    }
  }, [open]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!pin) { setError('Podaj PIN'); return; }
    setBusy(true); setError('');
    try {
      const r = await axios.post(`${API}/api/users/${userId}/verify-pin`, { pin });
      if (r.data?.valid) {
        sessionStorage.setItem(SESSION_KEY, '1');
        toast.success('🔓 Edytor menu odblokowany');
        onSuccess?.();
      } else if (r.data?.reason === 'no_pin_set') {
        setError('Nie masz ustawionego PIN-u. Ustaw w Ustawieniach → Konto.');
      } else {
        setError('Nieprawidłowy PIN');
      }
    } catch (err) {
      setError(err?.response?.data?.detail || 'Błąd weryfikacji');
    } finally { setBusy(false); }
  };

  if (!open) return null;

  return (
    <PortalOverlay onClose={onCancel}>
      <div onClick={onCancel} style={{
        position: 'fixed', inset: 0, zIndex: 2147483000,
        background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
      }}>
        <form onSubmit={submit} onClick={(e) => e.stopPropagation()} data-testid="sidebar-customizer-pin-gate" style={{
          background: 'var(--panel-bg, #1a1f2b)', color: 'var(--text)',
          borderRadius: 14, padding: 28,
          width: 'min(92vw, 360px)',
          border: '1px solid rgba(212,175,55,0.35)',
          boxShadow: '0 30px 100px rgba(0,0,0,0.7)',
        }}>
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <div style={{ fontSize: 40, marginBottom: 8 }}>🔒</div>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>Personalizacja menu</h3>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
              Podaj swój PIN, aby edytować widoczność modułów.
            </div>
          </div>

          <input
            ref={inputRef}
            type="password"
            inputMode="numeric"
            autoComplete="off"
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
            placeholder="••••"
            maxLength={12}
            data-testid="sidebar-customizer-pin-input"
            style={{
              width: '100%', padding: '14px 16px',
              borderRadius: 10, border: `1px solid ${error ? '#ef4444' : 'var(--border, rgba(255,255,255,0.1))'}`,
              background: 'var(--bg-card, #0b0e14)', color: 'var(--text)',
              fontSize: 20, fontWeight: 700, letterSpacing: 8,
              textAlign: 'center', boxSizing: 'border-box',
            }}
          />

          {error && (
            <div style={{ marginTop: 10, color: '#ef4444', fontSize: 12, textAlign: 'center' }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <button type="button" onClick={onCancel} disabled={busy}
              style={{
                flex: 1, padding: '10px 14px', borderRadius: 8,
                background: 'transparent', color: 'var(--text-muted)',
                border: '1px solid var(--border, rgba(255,255,255,0.1))', cursor: 'pointer',
                fontSize: 13,
              }}
            >Anuluj</button>
            <button type="submit" disabled={busy || pin.length === 0} data-testid="sidebar-customizer-pin-submit"
              style={{
                flex: 1.6, padding: '10px 14px', borderRadius: 8,
                background: 'linear-gradient(135deg, var(--gold, #d4af37), #b8860b)',
                color: '#000', border: 'none', cursor: busy ? 'wait' : 'pointer',
                fontSize: 13, fontWeight: 800,
              }}
            >{busy ? '⏳' : '🔓 Odblokuj'}</button>
          </div>
        </form>
      </div>
    </PortalOverlay>
  );
};

export default PinGate;
